import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { UploadFileToS3Params, UploadFileToS3Response } from '../types';

/**
 * NX-6056: a presigned PUT that fails still resolves — `fetch` only rejects on
 * a network error, not on a 4xx/5xx. Without this check an expired credential
 * (`403 ExpiredToken`) or a rejected policy returned `success: true`, the row
 * saved the key, and the record pointed at an object that was never written.
 * The user saw a green toast and a broken image.
 */
const putToS3 = async (
  url: string,
  file: File,
  signal?: AbortSignal,
): Promise<void> => {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
    signal,
  });
  if (!res.ok) {
    // S3 answers with an XML <Error><Code>…</Code></Error>; surface the code
    // so "ExpiredToken" or "AccessDenied" reaches the log instead of a bare 403.
    const body = await res.text().catch(() => '');
    const code = body.match(/<Code>([^<]+)<\/Code>/)?.[1];
    throw new Error(
      `Upload failed (${res.status}${code ? ` ${code}` : ''}). Please try again.`,
    );
  }
};

export const uploadFileToS3 = createAsyncThunk<
  UploadFileToS3Response,
  UploadFileToS3Params
>(
  'common/uploadFileToS3',
  async (params: UploadFileToS3Params, { rejectWithValue }) => {
    try {
      const { file, purpose } = params;
      const getSignedS3Response = await jwtAxios.get(endPoints.uploadFile, {
        params: {
          // fileType is the browser MIME type, which is the same for a company
          // logo and a scanned passport — the backend keys access off `purpose`
          // instead, and defaults to private when it is absent (NX-6056).
          fileType: file?.type || 'common',
          fileExt: file?.name.split('.')?.length
            ? file?.name.split('.')[file?.name.split('.').length - 1]
            : 'none',
          // axios omits undefined params, so an upload with no purpose
          // sends nothing and the backend keeps it private.
          purpose,
        },
      });
      const { url, key } = getSignedS3Response.data;
      // NX-6056: store the S3 object KEY. There is deliberately no fallback to
      // `url`: that is the presigned PUT, and a value derived from it — even
      // with the query stripped — is built from a short-lived credential and
      // dies with the signing session. It is also what let one object be saved
      // under two different hosts, the CDN copy being anonymously fetchable.
      // Fail loudly instead. The host is added back server-side on read.
      const URL_URL_AWS = key;
      if (!URL_URL_AWS) {
        throw new Error('Upload file failed, please try again!');
      }
      await putToS3(url, file);

      return {
        url: URL_URL_AWS,
        success: true,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const commonActs = {
  uploadFileToS3,
};
